import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { products, collections, heroData, philosophyQuote } from '../mock/mock';
import Reveal from '../components/Reveal';
import SmoothImage from '../components/SmoothImage';

const Home = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.hash]);

  const newArrivals = products.slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden bg-neutral-200">
        <div className="absolute inset-0 hero-ken">
          <SmoothImage
            src={heroData.image}
            alt="Andobrée Summer 2026"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full"
            style={{ objectPosition: 'center 30%' }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30" />
        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24">
          <Reveal variant="fade" duration={800}>
            <p className="text-[11px] tracking-[0.28em] uppercase text-white/90 mb-6">
              {heroData.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={140} duration={900}>
            <h1 className="font-display text-white text-6xl md:text-7xl lg:text-8xl font-medium leading-[1.02] tracking-tight max-w-3xl">
              {heroData.title}
            </h1>
          </Reveal>
          <Reveal delay={320} duration={800}>
            <Link
              to="/shop"
              className="inline-block mt-12 text-[11px] tracking-[0.28em] uppercase text-white border-b border-white pb-2 w-fit hover:tracking-[0.32em] transition-all duration-500"
            >
              {heroData.cta}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section id="new-arrivals" className="py-32 px-6 lg:px-12 scroll-mt-24">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-16">
            <Reveal variant="fade" duration={600}>
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-5 text-center">
                Just Landed
              </p>
            </Reveal>
            <Reveal delay={100}>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <h2 className="font-display text-5xl md:text-6xl font-medium tracking-tight">
                  New Arrivals
                </h2>
                <Link
                  to="/shop"
                  className="text-[11px] tracking-[0.22em] uppercase border-b border-neutral-900 pb-1 hover:opacity-60 transition-opacity"
                >
                  View All
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {newArrivals.map((p, i) => (
              <Reveal key={p.id} delay={i * 110} duration={750}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section id="our-collections" className="py-32 px-6 lg:px-12 bg-[#fafaf8] scroll-mt-24">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <Reveal variant="fade" duration={600}>
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-5">
                Curated For You
              </p>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-5xl md:text-6xl font-medium tracking-tight">
                Our Collections
              </h2>
            </Reveal>
          </div>
          <div className={`grid gap-6 lg:gap-10 ${collections.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 md:grid-cols-3'}`}>
            {collections.map((c, i) => (
              <Reveal key={c.id} delay={i * 130} duration={850}>
                <Link to={c.lookbook ? `/collection/${c.id.replace(/_/g, '-')}` : `/shop?collection=${c.id}`} className="group block">
                  <div className="aspect-[4/5] mb-6 overflow-hidden bg-neutral-100">
                    <SmoothImage
                      src={c.image}
                      alt={c.name}
                      className="w-full h-full"
                      imgClassName=""
                    />
                  </div>
                  <h3 className="font-display text-2xl font-medium mb-1">{c.name}</h3>
                  <p className="text-sm text-neutral-500">{c.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-40 px-6 bg-neutral-100">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal variant="fade" duration={600}>
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-10">
              Our Philosophy
            </p>
          </Reveal>
          <Reveal delay={150} duration={1000}>
            <blockquote className="font-display italic text-4xl md:text-5xl lg:text-6xl font-light text-neutral-500 leading-tight tracking-tight">
              &ldquo;{philosophyQuote}&rdquo;
            </blockquote>
          </Reveal>
          <Reveal delay={400} variant="fade" duration={600}>
            <div className="w-12 h-px bg-neutral-400 mx-auto mt-12" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const ProductCard = ({ product }) => (
  <Link to={`/product/${product.id}`} className="group block">
    <div className="relative aspect-[3/4] mb-5 overflow-hidden bg-neutral-100">
      <SmoothImage
        src={product.image}
        alt={product.name}
        className="w-full h-full"
        imgClassName=""
      />
      {product.onSale && (
        <span className="absolute top-4 left-4 z-10 bg-white px-3 py-1 text-[10px] tracking-[0.2em] uppercase">
          Sale
        </span>
      )}
    </div>
    <h3 className="text-sm font-medium">{product.name}</h3>
    <div className="flex gap-2 text-sm text-neutral-700 mt-1">
      <span>${product.price}</span>
      {product.originalPrice && (
        <span className="line-through text-neutral-400">${product.originalPrice}</span>
      )}
    </div>
  </Link>
);

export default Home;
